import { useSession } from "./useSession";
import { useAppointmentResponse } from "./useAppointmentResponse";
import { ApiService as api } from "../services/ApiService";
import { useGeo } from "./useGeo";
import { getYMD } from "../services/formatters";
import { useStates } from "./useStates";
import { useNavigate } from "react-router-dom";
import urls from "../config/urls.json";
import { isProd } from "../services/env";
import moment from "moment";
import { init as initResilienza } from "@monforte/resilienza-package";

export const useExternalParams = () => {
    const { appendToPixelSteps, read, update, batchUpdate, isAppointmentPayloadReady, isAppointmentPayloadAlmostReady, canWeSkipLuogoPage } = useSession();
    const { handleAppointmentSubmission } = useAppointmentResponse();
    const { states, setCurrentState } = useStates();
    const { geoCode } = useGeo();
    const navigate = useNavigate();

    const handleExternalParams = async () => {
        return new Promise(async (res, rej) => {
            initResilienza({
                url: process.env.REACT_APP_RESILIENZA_URL,
                defaultTimeout: process.env.REACT_APP_RESILIENZA_TIMEOUT,
            });

            const urlSearchParams = new URLSearchParams(window.location.search);
            const params = Object.fromEntries(urlSearchParams.entries());
            // no external params
            if (!Object.keys(params).length) {
                res();
                return;
            }

            // ?nome=gian&cognome=inge&email=gian@inge.com&telefono=3331122339&prodotto=99&mediacode=9999&utm_history=hist&utm_term=term&utm_source=src&utm_medium=med&utm_campaign=camp&utm_date=datt&utm_content=cont&dati_personali=True&informativa=True&societa_terze=False&sic=True

            // ORDINE DELLE COSE:
            //    1. riempire il session con TUTTO quello che posso ricavare nel formato corretto
            //    2. capire quali step posso saltare (quale percorso prendere)

            // 1. lista di campi che posso mergiare tal quali e non influenzano flusso
            //    [nome,cognome,email,prodotto,mediacode,utm*]
            let mergableFields = {};

            // 1b. "IsForzosoClink"
            if (Object.keys(params).includes("IsForzosoClink")) {
                if (params["IsForzosoClink"] == "True" || params["IsForzosoClink"] == "true" || params["IsForzosoClink"] == true || params["IsForzosoClink"] == "1") {
                    mergableFields["IsForzosoClink"] = true;
                }
            }

            const readyFields = ["nome", "cognome", "email", "prodotto", "utm_history", "utm_term", "mediacode", "utm_source", "utm_medium", "utm_campaign", "utm_date", "utm_content"];
            readyFields.forEach((field) => {
                if (Object.keys(params).includes(field)) {
                    mergableFields[field] = params[field];
                }
            });

            // 2. lista di campi che prima di mergiare devo fiformattare per session e non influenzano flusso
            //    [telefono,dati_personali,informativa,societa_terze,sic,clink,ricontatto]
            // ?nome=giannnn&telefono=222222223&email=asd@sa.com&ricontatto=True&dati_personali=True&informativa=False
            const toFormatFieldsAndValues = {
                telefono: "cellulare",
                ricontatto: "ricontatto_external",
                clink: "ricontatto_external",
                clink_ricontatto: "ricontatto_external",
                dati_personali: "privacyExtcommoffer",
                informativa: "privacyOtherservice",
                societa_terze: "privacyProfilazione",
                sic: "privacyInterncommoffer",
            };
            Object.keys(toFormatFieldsAndValues).forEach((field) => {
                if (Object.keys(params).includes(field)) {
                    mergableFields[toFormatFieldsAndValues[field]] =
                        params[field] == "True" || params[field] == "true" ? "1" : params[field] == "False" || params[field] == "false" ? "0" : params[field];
                }
            });

            // 2b. campi che richiedono maggiore sforzo per essere formattati correttamente
            // data_appuntamento, orario_appuntamento
            // ?data_appuntamento=2023-6-1&orario_appuntamento=14:30
            const hardFields = ["data_appuntamento", "orario_appuntamento"];
            hardFields.forEach((field) => {
                if (Object.keys(params).includes(field)) {
                    if ("data_appuntamento" == field) {
                        // @todo ISO "substract" 26 hours from out timezone
                        let tmpDateComponents = [];
                        params["data_appuntamento"].split("-").forEach((_) => {
                            tmpDateComponents.push(_.padStart(2, "0"));
                        });

                        let correctDate = new Date(tmpDateComponents.join("-"));
                        const year = correctDate.getFullYear();
                        const month = `${correctDate.getMonth() + 1}`.padStart(2, "0");
                        const day = `${correctDate.getDate()}`.padStart(2, "0");

                        mergableFields["dataScelta"] = {
                            data: `${year}-${month}-${day}T00:00:00`,
                            disponibilita: null,
                        };
                    } else if ("orario_appuntamento" == field) {
                        const splitted = decodeURIComponent(params[field]).split(":");
                        mergableFields["orarioScelto"] = {
                            hour: parseInt(splitted[0]),
                            minute: parseInt(splitted[1]),
                            disponibile: true,
                        };
                    }
                }
            });

            // 2c. campi che richiedono chiamate ai servizi
            // token vari, cf, id_filiale, (cap|indirizzo|coordinate|latitudine+longitudine)
            if (Object.keys(params).includes("token")) {
                // completo+ricontatto ?utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&isB2B=true&prodotto=1&token=NlfdsviJdeXh2_klrYYCVVSu8Gd4FoG1yypfSR8gsE5IeOL7EQjdk6bIJAZBxBFmXg5H53A_CnT7QyWqQ6aHAq6jVow0gBx0jaUrdYwyuGPKxlYFXWzLWiH-d3R0bRInfAJdMSHdQs8igzlQ0SUyNqnMrFxgqG0AeZ59lRU0-CrynF8ovf3zOzJiquciJAwRpX9_8-nWM9sgGwLzzWR7KdW9_ZU9TaDjRE9GXEXljU9oq5K2AXUdTNkWn1FEUShz55_USxjTYnN4g2argGt2KeCryBBxqXXF1Khjzjv0HMscMj6ZPAD_GbbYTN8RTlsMXsInxB4MIyqe7BYebm0DTg==&luogo=PERUGIA&latitudine=43.46725082397461&longitudine=12.629349708557129&data_appuntamento=2023-7-8&orario_appuntamento=9:00&id_filiale=18&nome_filiale=Filiale%20di%20Perugia&informativa=False&dati_personali=False&societa_terze=False&sic=True&ricontatto=true
                // completo+noricontatto ?utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&isB2B=true&prodotto=1&token=NlfdsviJdeXh2_klrYYCVVSu8Gd4FoG1yypfSR8gsE5IeOL7EQjdk6bIJAZBxBFmXg5H53A_CnT7QyWqQ6aHAq6jVow0gBx0jaUrdYwyuGPKxlYFXWzLWiH-d3R0bRInfAJdMSHdQs8igzlQ0SUyNqnMrFxgqG0AeZ59lRU0-CrynF8ovf3zOzJiquciJAwRpX9_8-nWM9sgGwLzzWR7KdW9_ZU9TaDjRE9GXEXljU9oq5K2AXUdTNkWn1FEUShz55_USxjTYnN4g2argGt2KeCryBBxqXXF1Khjzjv0HMscMj6ZPAD_GbbYTN8RTlsMXsInxB4MIyqe7BYebm0DTg==&luogo=PERUGIA&latitudine=43.46725082397461&longitudine=12.629349708557129&data_appuntamento=2023-7-8&orario_appuntamento=9:00&id_filiale=18&nome_filiale=Filiale%20di%20Perugia&informativa=False&dati_personali=False&societa_terze=False&sic=True
                // quasi ?utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&isB2B=true&prodotto=1&token=NlfdsviJdeXh2_klrYYCVVSu8Gd4FoG1yypfSR8gsE5IeOL7EQjdk6bIJAZBxBFmXg5H53A_CnT7QyWqQ6aHAq6jVow0gBx0jaUrdYwyuGPKxlYFXWzLWiH-d3R0bRInfAJdMSHdQs8igzlQ0SUyNqnMrFxgqG0AeZ59lRU0-CrynF8ovf3zOzJiquciJAwRpX9_8-nWM9sgGwLzzWR7KdW9_ZU9TaDjRE9GXEXljU9oq5K2AXUdTNkWn1FEUShz55_USxjTYnN4g2argGt2KeCryBBxqXXF1Khjzjv0HMscMj6ZPAD_GbbYTN8RTlsMXsInxB4MIyqe7BYebm0DTg==&luogo=PERUGIA&latitudine=43.46725082397461&longitudine=12.629349708557129&data_appuntamento=2023-7-8&orario_appuntamento=9:00&id_filiale=18&nome_filiale=Filiale%20di%20Perugia&informativa=False&dati_personali=False&societa_terze=False
                // incompleto ?utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&isB2B=true&token=NlfdsviJdeXh2_klrYYCVVSu8Gd4FoG1yypfSR8gsE5IeOL7EQjdk6bIJAZBxBFmXg5H53A_CnT7QyWqQ6aHAq6jVow0gBx0jaUrdYwyuGPKxlYFXWzLWiH-d3R0bRInfAJdMSHdQs8igzlQ0SUyNqnMrFxgqG0AeZ59lRU0-CrynF8ovf3zOzJiquciJAwRpX9_8-nWM9sgGwLzzWR7KdW9_ZU9TaDjRE9GXEXljU9oq5K2AXUdTNkWn1FEUShz55_USxjTYnN4g2argGt2KeCryBBxqXXF1Khjzjv0HMscMj6ZPAD_GbbYTN8RTlsMXsInxB4MIyqe7BYebm0DTg==&informativa=False&dati_personali=False&societa_terze=False&sic=True
                if (Object.keys(params).includes("isB2B")) {
                    const payloadDecode = {
                        token: params["token"],
                        isb2b: true,
                    };

                    payloadDecode["AppCode"] = "TYPDS";
                    if (Object.keys(params).includes("AppCode")) {
                        payloadDecode["AppCode"] = params["AppCode"];
                    }

                    // const tokenResult = await api.tokenDecode(payloadDecode);
                    const tokenResult = await api.tokenDecodeB2BC5(payloadDecode);
                    const tokenData = tokenResult;
                    // if (tokenData && tokenData.resultCode == 0) {
                    if (tokenData) {
                        // error
                        // if (
                        //     !tokenData.verificaTokenResponse ||
                        //     !tokenData.verificaTokenResponse.elencoParametri ||
                        //     !tokenData.verificaTokenResponse.elencoParametri.length ||
                        //     tokenData.verificaTokenResponse.elencoParametri.filter((_) => !_.valore).length
                        // ) {
                        //     if (!isProd()) console.log("tokenKO");
                        // }
                        // // success
                        // else {
                        // const cfArray = tokenData.verificaTokenResponse.elencoParametri.filter((_) => _.keyCode == "fiscalCode" || _.keycode == "fiscalCode");
                        const cfArray = tokenData.elencoParametri.filter((_) => _.keyCode == "fiscalCode" || _.keycode == "fiscalCode");
                        if (cfArray.length) {
                            const codiceFiscale = cfArray[0].valore;
                            mergableFields["codiceFiscale"] = codiceFiscale;
                            mergableFields["codiceFiscale_external"] = codiceFiscale;
                            mergableFields["utenteRiconosciuto"] = null != codiceFiscale;
                            // const checkCfResult = await api.CheckByCFAsync({
                            //   cf: codiceFiscale,
                            // });
                            const checkCfResult = await api.GetUserDataAndAppointmentByCf({
                                CodiceFiscale: codiceFiscale,
                            });
                            // if (null !== checkCfResult.appuntamento.oppID) {
                            if (checkCfResult.appointmentData && !!checkCfResult.appointmentData.appointmentId) {
                                // mergableFields["oppID"] = checkCfResult.appuntamento.oppID;
                                mergableFields["oppID"] = checkCfResult.appointmentData.appointmentId;
                            }
                        }

                        // const _nome = tokenData.verificaTokenResponse.elencoParametri.filter((_) => _.keyCode == "firstName" || _.keycode == "firstName");
                        const _nome = tokenData.elencoParametri.filter((_) => _.keyCode == "firstName" || _.keycode == "firstName");
                        if (_nome.length) {
                            mergableFields["nome"] = _nome[0].valore;
                        }
                        // const _cognome = tokenData.verificaTokenResponse.elencoParametri.filter((_) => _.keyCode == "lastName" || _.keycode == "lastName");
                        const _cognome = tokenData.elencoParametri.filter((_) => _.keyCode == "lastName" || _.keycode == "lastName");
                        if (_cognome.length) {
                            mergableFields["cognome"] = _cognome[0].valore;
                        }
                        // const _email = tokenData.verificaTokenResponse.elencoParametri.filter((_) => _.keyCode == "email" || _.keycode == "email");
                        const _email = tokenData.elencoParametri.filter((_) => _.keyCode == "email" || _.keycode == "email");
                        if (_email.length) {
                            mergableFields["email"] = _email[0].valore;
                        }

                        // const _telefono = tokenData.verificaTokenResponse.elencoParametri.filter((_) => _.keyCode == "mobilePhone" || _.keycode == "mobilePhone");
                        const _telefono = tokenData.elencoParametri.filter((_) => _.keyCode == "mobilePhone" || _.keycode == "mobilePhone");
                        if (_telefono.length) {
                            mergableFields["cellulare"] = _telefono[0].valore;
                        }
                        // }
                    }
                }
                // completo ?isClink=true&utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&prodotto=1&token=-M-Uo0z9hpMPosaxRkOuqW92kH9dlmJtWyOPGpAFMm1NkX6ibi4BFTRVqeQZiaTZRxJsgQXf3YByxqVUPxSPU0HWqhkcFu24zBb_76FvpHaqsRVGxpNQ2X8Mhi8a0iVIAiz6nljM3cSZadcHinCi23JpRv0cPm64j_8-AtrkGazBL_J7SviGYCOh6QkDIYYo4GoHvZ279A6160Llg37DhE-ZW-9JoXXxMDV5U0YdfmbPJoiA0ZvfkwgmOCumWu4DifkwcOeWHlqFX9sKedoregyKZzCZfVQiIGyHkh1VsusxKXl4hLz-UWnsPIahVI1ZKlr_u1TxzdWM06KVKgXUEw==&luogo=MILANO&latitudine=46.07154083251953&longitudine=11.11424732208252&data_appuntamento=2023-6-1&orario_appuntamento=14:30&id_filiale=18&nome_filiale=Filiale%20di%20Milano%20Zavattari&informativa=True&dati_personali=True&societa_terze=False&sic=True
                // quasi ?isClink=true&utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&prodotto=1&token=-M-Uo0z9hpMPosaxRkOuqW92kH9dlmJtWyOPGpAFMm1NkX6ibi4BFTRVqeQZiaTZRxJsgQXf3YByxqVUPxSPU0HWqhkcFu24zBb_76FvpHaqsRVGxpNQ2X8Mhi8a0iVIAiz6nljM3cSZadcHinCi23JpRv0cPm64j_8-AtrkGazBL_J7SviGYCOh6QkDIYYo4GoHvZ279A6160Llg37DhE-ZW-9JoXXxMDV5U0YdfmbPJoiA0ZvfkwgmOCumWu4DifkwcOeWHlqFX9sKedoregyKZzCZfVQiIGyHkh1VsusxKXl4hLz-UWnsPIahVI1ZKlr_u1TxzdWM06KVKgXUEw==&luogo=MILANO&latitudine=46.07154083251953&longitudine=11.11424732208252&data_appuntamento=2023-6-1&orario_appuntamento=14:30&id_filiale=18&nome_filiale=Filiale%20di%20Milano%20Zavattari&informativa=True&dati_personali=True&societa_terze=False
                // quasi+ricontatto ?isClink=true&utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&prodotto=1&token=-M-Uo0z9hpMPosaxRkOuqW92kH9dlmJtWyOPGpAFMm1NkX6ibi4BFTRVqeQZiaTZRxJsgQXf3YByxqVUPxSPU0HWqhkcFu24zBb_76FvpHaqsRVGxpNQ2X8Mhi8a0iVIAiz6nljM3cSZadcHinCi23JpRv0cPm64j_8-AtrkGazBL_J7SviGYCOh6QkDIYYo4GoHvZ279A6160Llg37DhE-ZW-9JoXXxMDV5U0YdfmbPJoiA0ZvfkwgmOCumWu4DifkwcOeWHlqFX9sKedoregyKZzCZfVQiIGyHkh1VsusxKXl4hLz-UWnsPIahVI1ZKlr_u1TxzdWM06KVKgXUEw==&luogo=MILANO&latitudine=46.07154083251953&longitudine=11.11424732208252&data_appuntamento=2023-6-1&orario_appuntamento=14:30&id_filiale=18&nome_filiale=Filiale%20di%20Milano%20Zavattari&informativa=True&dati_personali=True&societa_terze=False&ricontatto=True
                // incompleto ?isClink=true&utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&prodotto=1&token=-M-Uo0z9hpMPosaxRkOuqW92kH9dlmJtWyOPGpAFMm1NkX6ibi4BFTRVqeQZiaTZRxJsgQXf3YByxqVUPxSPU0HWqhkcFu24zBb_76FvpHaqsRVGxpNQ2X8Mhi8a0iVIAiz6nljM3cSZadcHinCi23JpRv0cPm64j_8-AtrkGazBL_J7SviGYCOh6QkDIYYo4GoHvZ279A6160Llg37DhE-ZW-9JoXXxMDV5U0YdfmbPJoiA0ZvfkwgmOCumWu4DifkwcOeWHlqFX9sKedoregyKZzCZfVQiIGyHkh1VsusxKXl4hLz-UWnsPIahVI1ZKlr_u1TxzdWM06KVKgXUEw==&luogo=MILANO&latitudine=46.07154083251953&longitudine=11.11424732208252&data_appuntamento=2023-6-1&orario_appuntamento=14:30&id_filiale=18&nome_filiale=Filiale%20di%20Milano%20Zavattari&informativa=True&dati_personali=True&societa_terze=False&ricontatto=True
                else if (Object.keys(params).includes("isClink")) {
                    const tokenResult = await api.tokenDecode({
                        token: params["token"],
                        isClink: true,
                    });
                    // const tokenData = tokenResult.data;
                    const tokenData = tokenResult;

                    if (tokenData) {
                        const userData = tokenData.userData.getAppointmentDetailsResponse.datiCliente;
                        if (userData.codiceFiscale) {
                            const codiceFiscale = userData.codiceFiscale;
                            mergableFields["codiceFiscale"] = codiceFiscale;
                            mergableFields["codiceFiscale_external"] = codiceFiscale;
                            mergableFields["utenteRiconosciuto"] = null != codiceFiscale;
                            // const checkCfResult = await api.CheckByCFAsync({
                            //   cf: codiceFiscale,
                            // });
                            const checkCfResult = await api.GetUserDataAndAppointmentByCf({
                                CodiceFiscale: codiceFiscale,
                            });
                            // if (null !== checkCfResult.appuntamento.oppID) {
                            //   mergableFields["oppID"] = checkCfResult.appuntamento.oppID;
                            // }
                            if (checkCfResult.appointmentData && !!checkCfResult.appointmentData.appointmentId) {
                                mergableFields["oppID"] = checkCfResult.appointmentData.appointmentId;
                            }
                        }
                        if (userData.nome) {
                            mergableFields["nome"] = userData.nome;
                        }
                        if (userData.cognome) {
                            mergableFields["cognome"] = userData.cognome;
                        }
                        if (userData.email) {
                            mergableFields["email"] = userData.email;
                        }
                        if (userData.codFiliale) {
                            mergableFields["codFiliale_external"] = userData.codFiliale;
                        }
                        const telefono = userData.cellulare;
                        if (telefono) {
                            mergableFields["cellulare"] = telefono.replace(/\s/g, "");
                        }

                        // @maybe? Visto che isClink, presumo che stiamo forzando il clink
                        mergableFields["ricontattoCf"] = true;
                        mergableFields["ricontattoLuogo"] = true;
                    }
                }
                // nuovo token b2b del chatbot
                else if (Object.keys(params).includes("isB2BChat")) {
                    const tokenResult = await api.tokenDecodeB2BChat({
                        token: decodeURIComponent(params["token"]),
                        // isb2b: true,
                    });
                    const tokenData = tokenResult.elencoParametri;
                    if (tokenData) {
                        // error
                        if (
                            !tokenData.length
                            //   || tokenData.filter((_) => !_.valore).length
                        ) {
                            if (!isProd()) console.log("tokenKO");
                        }
                        // success
                        else {
                            const cfArray = tokenData.filter((_) => _.keycode == "codiceFiscale");
                            if (cfArray.length) {
                                const codiceFiscale = cfArray[0].valore;
                                mergableFields["codiceFiscale"] = codiceFiscale;
                                mergableFields["codiceFiscale_external"] = codiceFiscale;
                                mergableFields["utenteRiconosciuto"] = null != codiceFiscale;
                                // const checkCfResult = await api.CheckByCFAsync({
                                //   cf: codiceFiscale,
                                // });
                                const checkCfResult = await api.GetUserDataAndAppointmentByCf({
                                    CodiceFiscale: codiceFiscale,
                                });
                                // if (null !== checkCfResult.appuntamento.oppID) {
                                //   mergableFields["oppID"] = checkCfResult.appuntamento.oppID;
                                // }
                                if (checkCfResult.appointmentData && !!checkCfResult.appointmentData.appointmentId) {
                                    mergableFields["oppID"] = checkCfResult.appointmentData.appointmentId;
                                }
                            }

                            const _nome = tokenData.filter((_) => _.keycode == "nome");
                            if (_nome.length) {
                                mergableFields["nome"] = _nome[0].valore;
                            }
                            const _cognome = tokenData.filter((_) => _.keycode == "cognome");
                            if (_cognome.length) {
                                mergableFields["cognome"] = _cognome[0].valore;
                            }
                            const _email = tokenData.filter((_) => _.keycode == "email");
                            if (_email.length) {
                                mergableFields["email"] = _email[0].valore;
                            }

                            const _telefono = tokenData.filter((_) => _.keycode == "telefonoCell" || _.keycode == "user_phone");
                            if (_telefono.length) {
                                mergableFields["cellulare"] = _telefono[0].valore;
                            }

                            const _cap = tokenData.filter((_) => _.keycode == "user_cap");
                            if (_cap.length) {
                                params["cap"] = _cap[0].valore;
                            }
                        }
                    }
                }

                // completo ?utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&prodotto=1&token=-M-Uo0z9hpMPosaxRkOuqW92kH9dlmJtWyOPGpAFMm1NkX6ibi4BFTRVqeQZiaTZRxJsgQXf3YByxqVUPxSPU0HWqhkcFu24zBb_76FvpHaqsRVGxpNQ2X8Mhi8a0iVIAiz6nljM3cSZadcHinCi23JpRv0cPm64j_8-AtrkGazBL_J7SviGYCOh6QkDIYYo4GoHvZ279A6160Llg37DhE-ZW-9JoXXxMDV5U0YdfmbPJoiA0ZvfkwgmOCumWu4DifkwcOeWHlqFX9sKedoregyKZzCZfVQiIGyHkh1VsusxKXl4hLz-UWnsPIahVI1ZKlr_u1TxzdWM06KVKgXUEw==&luogo=MILANO&latitudine=46.07154083251953&longitudine=11.11424732208252&data_appuntamento=2023-6-1&orario_appuntamento=14:30&id_filiale=403&nome_filiale=Filiale%20di%20Milano%20Zavattari&informativa=True&dati_personali=True&societa_terze=False&sic=True
                // quasi ?utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&prodotto=1&token=-M-Uo0z9hpMPosaxRkOuqW92kH9dlmJtWyOPGpAFMm1NkX6ibi4BFTRVqeQZiaTZRxJsgQXf3YByxqVUPxSPU0HWqhkcFu24zBb_76FvpHaqsRVGxpNQ2X8Mhi8a0iVIAiz6nljM3cSZadcHinCi23JpRv0cPm64j_8-AtrkGazBL_J7SviGYCOh6QkDIYYo4GoHvZ279A6160Llg37DhE-ZW-9JoXXxMDV5U0YdfmbPJoiA0ZvfkwgmOCumWu4DifkwcOeWHlqFX9sKedoregyKZzCZfVQiIGyHkh1VsusxKXl4hLz-UWnsPIahVI1ZKlr_u1TxzdWM06KVKgXUEw==&luogo=MILANO&latitudine=46.07154083251953&longitudine=11.11424732208252&data_appuntamento=2023-6-1&orario_appuntamento=14:30&id_filiale=403&nome_filiale=Filiale%20di%20Milano%20Zavattari&informativa=True&dati_personali=True&societa_terze=False
                // quasi+ricontatto ?&utm_source=DIGITALSTRATEGY&utm_campaign=FORZOSOXZnuovaDS&mediacode=4492&prodotto=1&token=-M-Uo0z9hpMPosaxRkOuqW92kH9dlmJtWyOPGpAFMm1NkX6ibi4BFTRVqeQZiaTZRxJsgQXf3YByxqVUPxSPU0HWqhkcFu24zBb_76FvpHaqsRVGxpNQ2X8Mhi8a0iVIAiz6nljM3cSZadcHinCi23JpRv0cPm64j_8-AtrkGazBL_J7SviGYCOh6QkDIYYo4GoHvZ279A6160Llg37DhE-ZW-9JoXXxMDV5U0YdfmbPJoiA0ZvfkwgmOCumWu4DifkwcOeWHlqFX9sKedoregyKZzCZfVQiIGyHkh1VsusxKXl4hLz-UWnsPIahVI1ZKlr_u1TxzdWM06KVKgXUEw==&luogo=MILANO&latitudine=46.07154083251953&longitudine=11.11424732208252&data_appuntamento=2023-6-1&orario_appuntamento=14:30&id_filiale=403&nome_filiale=Filiale%20di%20Milano%20Zavattari&informativa=True&dati_personali=True&societa_terze=False&ricontatto=True
                else {
                    const tokenResult = await api.tokenDecode({ token: params["token"] });
                    // const tokenData = tokenResult.data;
                    const tokenData = tokenResult;
                    // console.log(tokenData);
                    if (tokenData && tokenData.codiceFiscale) {
                        mergableFields["codiceFiscale"] = tokenData.codiceFiscale;
                        mergableFields["codiceFiscale_external"] = tokenData.codiceFiscale;
                        mergableFields["utenteRiconosciuto"] = null != tokenData.codiceFiscale;
                        // const checkCfResult = await api.CheckByCFAsync({
                        //   cf: tokenData.codiceFiscale,
                        // });
                        const checkCfResult = await api.GetUserDataAndAppointmentByCf({
                            CodiceFiscale: tokenData.codiceFiscale,
                        });
                        // if (null !== checkCfResult.appuntamento.oppID) {
                        //   mergableFields["oppID"] = checkCfResult.appuntamento.oppID;
                        // }
                        if (checkCfResult.appointmentData && !!checkCfResult.appointmentData.appointmentId) {
                            mergableFields["oppID"] = checkCfResult.appointmentData.appointmentId;
                        }

                        if (tokenData.nome) {
                            mergableFields["nome"] = tokenData.nome;
                        }
                        if (tokenData.cognome) {
                            mergableFields["cognome"] = tokenData.cognome;
                        }
                        if (tokenData.email) {
                            mergableFields["email"] = tokenData.email;
                        }
                        const telefono = tokenData.telefono || tokenData.telefonoCell || tokenData.telefonoFisso;
                        if (telefono) {
                            mergableFields["cellulare"] = telefono.replace(/\s/g, "");
                        }
                        if (tokenData.codFiliale) {
                            mergableFields["codFiliale_external"] = tokenData.codFiliale;
                        }
                    }
                    if (tokenData && tokenData.cap) {
                        params["cap"] = tokenData.cap;
                    }
                }
            }

            if (Object.keys(params).includes("geocodeAccuracy")) {
                mergableFields["placeType"] = params.geocodeAccuracy;
            }
            if (Object.keys(params).includes("locality")) {
                mergableFields["city"] = params.locality;
            }
            if (Object.keys(params).includes("cap")) {
                mergableFields["cap"] = params.cap;
            }

            // ric ?cf=ngngmr88t21z112f
            // non ric ?cf=SNDMRA80A01F205R
            if (Object.keys(params).includes("cf")) {
                mergableFields["codiceFiscale"] = params["cf"];
                mergableFields["codiceFiscale_external"] = params["cf"];
                // const checkCfResult = await api.CheckByCFAsync({
                //   cf: params["cf"],
                // });
                const checkCfResult = await api.GetUserDataAndAppointmentByCf({
                    CodiceFiscale: params["cf"],
                });
                // if (null !== checkCfResult.appuntamento.oppID) {
                //   mergableFields["oppID"] = checkCfResult.appuntamento.oppID;
                //   mergableFields["oppID_external"] = checkCfResult.appuntamento.oppID;
                // }
                if (checkCfResult.appointmentData && !!checkCfResult.appointmentData.appointmentId) {
                    mergableFields["oppID"] = checkCfResult.appointmentData.appointmentId;
                    mergableFields["oppID_external"] = checkCfResult.appointmentData.appointmentId;
                } else {
                    // const userData = checkCfResult.appuntamento.utente;
                    const userData = checkCfResult.datiCliente;
                    // if (null !== userData.codice_fiscale) {
                    //   mergableFields["codiceFiscale_external"] = userData.codice_fiscale;
                    // }

                    // mergableFields["ricontattoCf"] = checkCfResult.ricontatto;
                    // mergableFields["utenteRiconosciuto"] =
                    //   null != userData.codice_fiscale;
                    // mergableFields["nome"] = userData.nome || "";
                    // mergableFields["cognome"] = userData.cognome || "";
                    // mergableFields["email"] = userData.email || "";
                    // mergableFields["cellulare"] = userData.telefono || "";
                    // mergableFields["capResidenza"] = userData.cap || "";
                    if (userData) {
                        mergableFields["ricontattoCf"] = checkCfResult.Ricontatto;
                        // mergableFields["utenteRiconosciuto"] = null != userData.codice_fiscale;
                        mergableFields["utenteRiconosciuto"] = !!userData.codiceFiscale;
                        mergableFields["nome"] = userData.nome || "";
                        mergableFields["cognome"] = userData.cognome || "";
                        mergableFields["email"] = userData.email || "";
                        // mergableFields["cellulare"] = userData.telefono || "";
                        mergableFields["cellulare"] = userData.cellulare || "";
                        // mergableFields["capResidenza"] = userData.cap || "";
                        mergableFields["capResidenza"] = userData.capResidenza || "";
                    }
                }
            }

            // campi per il luogo
            // ?id_filiale=18
            const codFiliale = params["id_filiale"] || params["id_filiale_direct"] || mergableFields["codFiliale_external"];
            if (codFiliale) {
                mergableFields["id_filiale_direct"] = codFiliale;
                mergableFields["codFiliale_external"] = codFiliale;
                let coordinate = {};
                coordinate = await api.TrovaFiliale({ IdFiliale: codFiliale });
                let filialiResult = {};
                filialiResult = await api.TrovaFiliali3({
                    // Coordinate: coordinate,
                    latitude: coordinate.lat,
                    longitude: coordinate.lng,
                    placeType: mergableFields["placeType"],
                    city: mergableFields["city"],
                    cap: mergableFields["cap"],
                });
                let filialeSceltaIdx = 0;
                const filialeScelta = filialiResult.filiali.filter((filiale, idx) => {
                    if (filiale.codFiliale == codFiliale) {
                        filialeSceltaIdx = idx;
                    }
                    return filiale.codFiliale == codFiliale;
                })[0];

                mergableFields["filialeSceltaIdx"] = filialeSceltaIdx;

                mergableFields = {
                    ...mergableFields,
                    filiali: filialiResult.filiali,
                    filialeScelta: filialeScelta,
                    disponibilita: filialeScelta.days,
                    // dataScelta: filialeScelta.days[0],
                    ricontattoLuogo: filialiResult.ricontatto,
                };

                if (!Object.keys(params).includes("data_appuntamento")) {
                    mergableFields = {
                        ...mergableFields,
                        dataScelta: filialeScelta.days[0],
                    };
                }

                let disponibilitaResult = {};
                disponibilitaResult = await api.Disponibilita({
                    CodFiliale: filialeScelta.codFiliale,
                    Giorno: getYMD(filialeScelta.days[0].data),
                });
                mergableFields = {
                    ...mergableFields,
                    orari: disponibilitaResult.orariDisponibili,
                    // orarioScelto: disponibilitaResult.orariDisponibili.filter(
                    //   (_) => _.disponibile == true
                    // )[0],
                };

                if (!Object.keys(params).includes("orario_appuntamento")) {
                    mergableFields = {
                        ...mergableFields,
                        orarioScelto: disponibilitaResult.orariDisponibili.filter((_) => _.disponibile == true)[0],
                    };
                }
            }

            // ?id_filiale_direct_c5=456290 (CodiceOcsPV)
            const codFilialeC5 = params["id_filiale_direct_c5"];
            if (codFilialeC5) {
                mergableFields["id_filiale_direct_c5"] = codFilialeC5;
                let filialiResult = [];
                // @todo rimettere
                filialiResult = await api.TrovaFilialiC5({
                    IdFiliale: `${codFilialeC5}`,
                });

                if (filialiResult && filialiResult.length) {
                    const filialeScelta = filialiResult[0];
                    update("cap_utente", filialeScelta.CAPUfficio);

                    const dispResult = await api.GetDayAndTimeSlot({
                        IdFiliale: codFilialeC5,
                        From: moment().toDate(),
                        To: moment().add(7, "days").toDate(),
                    });
                    // @todo aggiungere gestione dei campi c5/useLuogoPage:237
                    const Slots = dispResult.Slots;
                    let disponibilita = Slots || [];
                    if (disponibilita.length == 0) {
                        disponibilita = [
                            {
                                Date: moment().format(),
                                Time: [],
                            },
                        ];
                    }
                    let idx = 0;
                    for (let ds in disponibilita) {
                        idx++;
                        const disp = disponibilita[ds];

                        //Impostiamo la disponibilita per il primo giorno
                        const orari = [
                            { hour: 9, minute: 0, disponibile: false },
                            { hour: 9, minute: 30, disponibile: false },
                            { hour: 10, minute: 0, disponibile: false },
                            { hour: 10, minute: 30, disponibile: false },
                            { hour: 11, minute: 0, disponibile: false },
                            { hour: 11, minute: 30, disponibile: false },
                            { hour: 12, minute: 0, disponibile: false },
                            { hour: 12, minute: 30, disponibile: false },
                            { hour: 14, minute: 0, disponibile: false },
                            { hour: 14, minute: 30, disponibile: false },
                            { hour: 15, minute: 0, disponibile: false },
                            { hour: 15, minute: 30, disponibile: false },
                            { hour: 16, minute: 0, disponibile: false },
                            { hour: 16, minute: 30, disponibile: false },
                            { hour: 17, minute: 0, disponibile: false },
                            { hour: 17, minute: 30, disponibile: false },
                        ];
                        let primoDisponibile = null;
                        for (let t in disp.Time) {
                            const orarioDisponibile = disp.Time[t];
                            const ora = orarioDisponibile.split(":")[0];
                            const minuto = orarioDisponibile.split(":")[1];
                            for (let o in orari) {
                                if (orari[o].hour == ora && orari[o].minute == minuto) {
                                    orari[o].disponibile = true;
                                    if (primoDisponibile == null) {
                                        primoDisponibile = orari[o];
                                    }
                                }
                            }
                        }
                        // let's update orari only for the first filiale
                        if (idx == 1) {
                            batchUpdate({
                                orari: orari,
                                orarioScelto: primoDisponibile,
                            });
                        }

                        if (!filialeScelta.hasOwnProperty("days")) {
                            filialeScelta.days = [];
                        }
                        if (disp != "KO") {
                            filialeScelta.days.push({
                                data: disp.Date,
                                disponibilita: null,
                            });
                        }
                    }

                    if (!Object.keys(params).includes("data_appuntamento")) {
                        mergableFields = {
                            ...mergableFields,
                            dataScelta: filialeScelta.days[0],
                        };
                    }

                    mergableFields = {
                        ...mergableFields,
                        filiali: filialiResult,
                        filialeScelta: filialeScelta,
                        disponibilita: filialeScelta.days,
                        // dataScelta: filialeScelta.days[0],
                        ricontattoLuogo: false,
                    };
                }
            }

            // ?agente_direct_clink=asd
            const clinkDirect = params["agente_direct_clink"];
            if (clinkDirect) {
                // @test agente plurimandatario
                let agenteRes = null;
                if (clinkDirect == "23884821") {
                    mergableFields["agente_direct_clink_pluri"] = true;
                    agenteRes = {
                        Data: {
                            Codice: "23884821",
                            Nome: "SERGIO DEIANA",
                            Cellulare: null,
                            Email: null,
                            ElencoCAP: [],
                        },
                        Status: 0,
                        Message: null,
                    };
                } else {
                    agenteRes = await api.AgenteClinkDirect({ Codice: clinkDirect });
                }

                console.log(agenteRes);

                // se non va in errore
                if (0 == agenteRes.Status) {
                    mergableFields["agente_direct_clink"] = clinkDirect;

                    // @tmp
                    const agenteObject = {
                        // hubAgente: agenteRes.Data.CodiceHub,
                        nomeAgente: agenteRes.Data.Nome,
                    };

                    mergableFields = {
                        ...mergableFields,
                        ...agenteObject,
                    };
                }
            }

            // ?coordinate=45.4671753,9.1896644
            else if (Object.keys(params).includes("coordinate")) {
                const lat = params.coordinate.split(",")[0];
                const lng = params.coordinate.split(",")[1];
                let filialiResult = {};
                try {
                    filialiResult = await api.TrovaFiliali3({
                        latitude: lat,
                        longitude: lng,
                        placeType: mergableFields["placeType"],
                        city: mergableFields["city"],
                        cap: mergableFields["cap"],
                    });
                    mergableFields = {
                        ...mergableFields,
                        filiali: filialiResult.filiali,
                        filialeScelta: filialiResult.filiali[0],
                        disponibilita: filialiResult.filiali[0].days,
                        dataScelta: filialiResult.filiali[0].days[0],
                        ricontattoLuogo: filialiResult.ricontatto,
                    };

                    let disponibilitaResult = {};
                    disponibilitaResult = await api.Disponibilita({
                        CodFiliale: filialiResult.filiali[0].codFiliale,
                        Giorno: getYMD(filialiResult.filiali[0].days[0].data),
                    });
                    mergableFields = {
                        ...mergableFields,
                        orari: disponibilitaResult.orariDisponibili,
                        orarioScelto: disponibilitaResult.orariDisponibili.filter((_) => _.disponibile == true)[0],
                    };
                } catch (error) {
                    if (!isProd()) console.log(error);
                }
            }
            // ?cap=20135
            else if (Object.keys(params).includes("cap") && params["cap"] != "") {
                let coordinate = {};
                try {
                    coordinate = await api.getCoordsByCap(params["cap"]);
                    if (Object.keys(params).includes("indirizzo")) {
                        const geoResult = await geoCode(params["indirizzo"]);
                        coordinate["locality"] = geoResult.locality;
                    }
                    let filialiResult = {};
                    filialiResult = await api.TrovaFiliali3({
                        cap: params["cap"],
                        city: coordinate.locality,
                        latitude: coordinate.lat,
                        longitude: coordinate.lng,
                        placeType: mergableFields["placeType"],
                        city: mergableFields["city"],
                        cap: mergableFields["cap"],
                    });
                    const filialeScelta = filialiResult.filiali[0];
                    mergableFields = {
                        ...mergableFields,
                        cap: params["cap"],
                        filiali: filialiResult.filiali,
                        filialeScelta,
                        disponibilita: filialeScelta.days,
                        // dataScelta: filialiResult.filiali[0].days[0],
                        ricontattoLuogo: filialiResult.ricontatto,
                    };
                    if (!Object.keys(params).includes("data_appuntamento")) {
                        mergableFields = {
                            ...mergableFields,
                            dataScelta: filialeScelta.days[0],
                        };
                    }

                    let disponibilitaResult = {};
                    disponibilitaResult = await api.Disponibilita({
                        CodFiliale: filialiResult.filiali[0].codFiliale,
                        Giorno: getYMD(filialiResult.filiali[0].days[0].data),
                    });
                    mergableFields = {
                        ...mergableFields,
                        orari: disponibilitaResult.orariDisponibili,
                        // orarioScelto: disponibilitaResult.orariDisponibili.filter(
                        //   (_) => _.disponibile == true
                        // )[0],
                    };
                    if (!Object.keys(params).includes("orario_appuntamento")) {
                        mergableFields = {
                            ...mergableFields,
                            orarioScelto: disponibilitaResult.orariDisponibili.filter((_) => _.disponibile == true)[0],
                        };
                    }
                } catch (error) {
                    if (!isProd()) console.log(error);
                }
            }

            // ?indirizzo=Italy%2C+Verona%2C+Italia&postcode=&latitudine=45.4671753&longitudine=9.1896644&prodotto=1&mediacode=4035
            else if (Object.keys(params).includes("indirizzo")) {
                let coordinate = {};
                try {
                    coordinate = await geoCode(params["indirizzo"]);
                    let filialiResult = {};
                    filialiResult = await api.TrovaFiliali3({
                        latitude: coordinate.lat,
                        longitude: coordinate.lng,
                        placeType: mergableFields["placeType"],
                        city: mergableFields["city"],
                        cap: mergableFields["cap"],
                    });
                    const filialeScelta = filialiResult.filiali[0];
                    mergableFields = {
                        ...mergableFields,
                        filiali: filialiResult.filiali,
                        filialeScelta,
                        disponibilita: filialeScelta.days,
                        // dataScelta: filialiResult.filiali[0].days[0],
                        ricontattoLuogo: filialiResult.ricontatto,
                    };
                    if (!Object.keys(params).includes("data_appuntamento")) {
                        mergableFields = {
                            ...mergableFields,
                            dataScelta: filialeScelta.days[0],
                        };
                    }

                    let disponibilitaResult = {};
                    disponibilitaResult = await api.Disponibilita({
                        CodFiliale: filialiResult.filiali[0].codFiliale,
                        Giorno: getYMD(filialiResult.filiali[0].days[0].data),
                    });
                    mergableFields = {
                        ...mergableFields,
                        orari: disponibilitaResult.orariDisponibili,
                        // orarioScelto: disponibilitaResult.orariDisponibili.filter(
                        //   (_) => _.disponibile == true
                        // )[0],
                    };
                    if (!Object.keys(params).includes("orario_appuntamento")) {
                        mergableFields = {
                            ...mergableFields,
                            orarioScelto: disponibilitaResult.orariDisponibili.filter((_) => _.disponibile == true)[0],
                        };
                    }
                } catch (error) {
                    if (!isProd()) console.log(error);
                }
            }
            batchUpdate(mergableFields);

            // --------

            // if there was an error, let's skip this page and continue with the usual flow
            if (mergableFields["agente_direct_clink"]) {
                // update("prodotto", 1);
                console.log(mergableFields);
                res(() => {
                    navigate(urls.clinkDirect.path, { replace: true });
                });
                return;
            }

            // -3. !TOKENS - check per flusso completo isAppointmentPayloadReady
            //    [tutti]
            if (isAppointmentPayloadReady()) {
                appendToPixelSteps(["/home", "/scegli-filiale", "/codice-fiscale", "/dati-personali-*"]);
                res(handleAppointmentSubmission);
                return;
            }

            // stessa logica di filialipage che conosce cf
            // 4,
            if (isAppointmentPayloadAlmostReady()) {
                setCurrentState(states.FETCH_CF_END);
                if (true == read("ricontattoCf") && true == read("ricontattoLuogo")) {
                    update("ricontatto", true);
                    res(() => {
                        appendToPixelSteps(["/home", "/scegli-filiale", "/codice-fiscale"]);
                        navigate(urls.clink.path, { replace: true });
                    });
                } else if (true == read("utenteRiconosciuto")) {
                    res(() => {
                        appendToPixelSteps(["/home", "/scegli-filiale", "/codice-fiscale"]);
                        navigate(urls.datiPersonaliRiconosciuto.path, { replace: true });
                    });
                } else {
                    res(() => {
                        appendToPixelSteps(["/home", "/scegli-filiale", "/codice-fiscale"]);
                        navigate(urls.datiPersonaliNonRiconosciuto.path, { replace: true });
                    });
                }
                return;
            }

            if (canWeSkipLuogoPage()) {
                setCurrentState(states.FETCH_FILIALI_END);
                res(() => {
                    if (params["id_filiale_direct"]) {
                        update("prodotto", 1);
                        navigate(urls.filialiDirect.path, { replace: true });
                    } else if (params["id_filiale_direct_c5"]) {
                        //Flusso c5
                        update("prodotto", 3);
                        navigate(urls.filialiDirectC5.path, { replace: true });
                    } else {
                        appendToPixelSteps(["/home"]);
                        navigate(urls.filiali.path, { replace: true });
                    }
                });
                return;
            }

            //Flusso business
            if (params.hasOwnProperty("prodotto") && [5, "5"].indexOf(params.prodotto) != -1) {
                update("prodotto", 5);
                res(() => {
                    appendToPixelSteps(["/home"]);
                    navigate(urls.prestitoBusiness.path, { replace: true });
                });
                return;
            }

            //Flusso c5
            if (params.hasOwnProperty("prodotto") && [3, "3"].indexOf(params.prodotto) != -1) {
                update("prodotto", 3);
                res(() => {
                    navigate(urls.codiceFiscaleC5.path, { replace: true });
                });
                return;
            }

            res();
        });
    };

    return {
        handleExternalParams,
    };
};
